import React from "react";
import { Alert, Box, Button, Card, CardContent, Stack, Typography } from "@mui/material";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import LockClockRoundedIcon from "@mui/icons-material/LockClockRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import { useNavigate } from "react-router-dom";
import { useProjects } from "../../context/ProjectsContext";
import { getProjectBillingAccess } from "../../utils/projectBillingAccess";
import ContextualHelp from "./ContextualHelp";

const BRAND = { orange: "#F35A3A", dark: "var(--wp-panel-text-primary, #19242F)" };

export default function ModuleDisabledState({ projectId, title, description }) {
  const navigate = useNavigate();
  const { projects, activeProject } = useProjects();

  const project = React.useMemo(() => {
    const matched = projects.find(
      (item) => item.slug === projectId || item.id === projectId || item._id === projectId,
    );
    const activeMatches =
      activeProject?.slug === projectId ||
      activeProject?.id === projectId ||
      activeProject?._id === projectId;
    return matched || (activeMatches ? activeProject : null);
  }, [projects, activeProject, projectId]);

  const access = getProjectBillingAccess(project);
  const paymentRestricted = access.isLandingOnly || access.isSuspended;

  return (
    <Card elevation={0} sx={{ borderRadius: 4, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),0.08)" }}>
      <CardContent sx={{ p: { xs: 3, md: 5 } }}>
        <Stack spacing={2.5} alignItems="flex-start">
          <Box sx={{ width: 58, height: 58, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,0.1)", color: BRAND.orange }}>
            {paymentRestricted ? <LockClockRoundedIcon /> : <LockRoundedIcon />}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="h4" sx={{ fontWeight: 950, color: BRAND.dark }}>
                {paymentRestricted
                  ? `${title} está temporalmente pausado`
                  : `${title} todavía no está activo`}
              </Typography>
              {!paymentRestricted && description ? (
                <ContextualHelp
                  compact
                  title={`Qué habilita ${title}`}
                  text={description}
                  bullets={[
                    "La disponibilidad depende de los módulos activos del proyecto.",
                    "Antes de confirmar un cambio de plan vas a ver el total actualizado.",
                  ]}
                />
              ) : null}
            </Stack>
            <Typography sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),0.65)", mt: 0.8, maxWidth: 720 }}>
              {paymentRestricted
                ? "La landing sigue visible y tus datos están seguros. Regularizá la mensualidad para recuperar esta función."
                : "Esta herramienta no forma parte de la configuración actual del proyecto."}
            </Typography>
          </Box>
          <Alert severity={paymentRestricted ? "warning" : "info"} sx={{ width: "100%" }}>
            {paymentRestricted
              ? "Al aprobarse el pago, WebProfit restaura el acceso sin borrar la configuración existente."
              : "Vas a ver el nuevo total antes de confirmar cualquier cambio de plan."}
          </Alert>
          <Button
            startIcon={paymentRestricted ? <CreditCardRoundedIcon /> : <WorkspacePremiumRoundedIcon />}
            onClick={() =>
              navigate(
                paymentRestricted
                  ? `/dashboard/proyectos/${projectId}/configuracion/suscripcion`
                  : `/dashboard/proyectos/${projectId}/configuracion/suscripcion?feature=${encodeURIComponent(title)}`,
              )
            }
            variant="contained"
            sx={{ bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900 }}
          >
            {paymentRestricted ? "Regularizar y reactivar" : "Ver precio y activar"}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}
